import React, { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '../../darkmode/DarkModeContext';

const ChatSupport = () => {
  const { isDarkMode } = useDarkMode();
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      type: 'bot',
      message: 'Halo! Selamat datang di Official telnet Customer Service 24/7. 🤖\n\nSaya adalah AI Assistant yang siap membantu dengan semua layanan Telnet dan paket internet kami.\n\n**Layanan yang bisa saya bantu:**\n🏠 Paket Internet Rumahan\n🏢 Solusi Bisnis & Enterprise  \n📺 IPTV 300+ Channel\n🎮 Gaming Low Latency\n📡 Starlink Area Remote\n📹 CCTV & Security\n🔧 Technical Support 24/7\n\nAda yang bisa saya bantu hari ini?',
      time: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const messagesEndRef = useRef(null);

  // Get backend URL
  const getBackendURL = () => {
    return import.meta.env.VITE_API_URL || 'http://localhost:5000';
  };

  const BACKEND_URL = getBackendURL();

  const quickTemplates = [
    "Paket internet rumahan apa saja?",
    "Cara daftar paket bisnis",
    "Troubleshoot internet lambat", 
    "Harga dan channel IPTV",
    "Paket gaming untuk ML/FF",
    "Starlink untuk pedesaan",
    "Instalasi CCTV berapa?"
  ];

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      console.log(' Checking connection to:', `${BACKEND_URL}/api/chatbot/status`);
      
      const response = await fetch(`${BACKEND_URL}/api/chatbot/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log(' Status response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log(' Connected:', data);
        setConnectionStatus('connected');
      } else {
        console.warn(' Non-OK response:', response.status);
        setConnectionStatus('fallback');
      }
    } catch (error) {
      console.error(' Connection check failed:', error);
      setConnectionStatus('fallback');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isTyping]);

  const sendToChatAPI = async (userMessage, chatHistory) => {
    const url = `${BACKEND_URL}/api/chatbot/chat`;
    console.log('📤 Sending to:', url);
    console.log('📦 Payload:', { message: userMessage, historyLength: chatHistory.length });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          chatHistory: chatHistory
        }),
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Success response:', data);
      
      if (data.success) {
        return data.response;
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('💥 API Call Error:', error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: newMessage,
      time: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages((prev) => [...prev, userMessage]);
    const currentMessage = newMessage;
    setNewMessage('');
    setIsTyping(true);

    try {
      const recentHistory = chatMessages.slice(-6).map(msg => ({
        type: msg.type,
        message: msg.message
      }));

      const botResponseText = await sendToChatAPI(currentMessage, recentHistory);
      
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        message: botResponseText,
        time: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
      };
      
      setChatMessages((prev) => [...prev, botResponse]);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to get AI response:', error);
      
      const fallbackResponse = {
        id: Date.now() + 1,
        type: 'bot',
        message: `🤖 **NETTV ASSISTANT**\n\nMaaf, terjadi gangguan koneksi sementara.\n\n**Informasi Kontak:**\n📞 Customer Service: 1500-123\n💬 WhatsApp: 0812-3456-7890\n✉️ Email: support@nettv.id\n\n**Troubleshooting:**\n1. Pastikan backend server berjalan di port 5000\n2. Check console untuk detail error\n3. Verifikasi CORS settings\n\nSilakan coba lagi dalam beberapa saat.`,
        time: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
      };
      
      setChatMessages((prev) => [...prev, fallbackResponse]);
      setConnectionStatus('disconnected');
    } finally {
      setIsTyping(false);
    }
  };

  const handleTemplateClick = (template) => {
    setNewMessage(template);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getStatusInfo = () => {
    switch(connectionStatus) {
      case 'connected':
        return { text: 'AI Connected', color: 'bg-green-500', dot: 'bg-green-400' };
      case 'fallback':
        return { text: 'Standard Mode', color: 'bg-green-500', dot: 'bg-green-400' };
      case 'disconnected':
        return { text: 'Disconnected', color: 'bg-red-500', dot: 'bg-red-400' };
      case 'checking':
        return { text: 'Checking...', color: 'bg-yellow-500', dot: 'bg-yellow-400' };
      default:
        return { text: 'Standard Mode', color: 'bg-green-500', dot: 'bg-green-400' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={`fixed inset-0 w-full h-full flex flex-col overflow-hidden transition-all duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <div className={`flex-shrink-0 p-4 border-b transition-all duration-300 ${
        isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'
      }`}>
        <div className="flex justify-between items-center">
          <h3 className={`text-lg sm:text-xl font-bold flex items-center gap-2 sm:gap-3 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            🤖 Telnet AI Assistant
            <span className={`text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full text-white ${statusInfo.color} transition-all duration-300`}>
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${statusInfo.dot} animate-pulse`}></span>
                {statusInfo.text}
              </span>
            </span>
          </h3>
        </div>
        {connectionStatus === 'fallback' && (
          <p className={`text-xs mt-2 transition-colors duration-300 ${
            isDarkMode ? 'text-green-400' : 'text-green-600'
          }`}>
             Backend URL: {BACKEND_URL || 'Using relative path'}
          </p>
        )}
      </div>

      {/* Chat Messages */}
      <div className={`flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        {chatMessages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] lg:max-w-2xl px-3 sm:px-4 py-2 sm:py-3 rounded-xl break-words whitespace-pre-line transition-all duration-300 ${
                message.type === 'user'
                  ? isDarkMode 
                    ? 'bg-green-600 text-white transform hover:scale-[1.02]' 
                    : 'bg-green-500 text-white transform hover:scale-[1.02]'
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-100 border border-gray-700 shadow-lg' 
                    : 'bg-white text-gray-900 border border-gray-200 shadow-lg'
              }`}
            >
              <div className="text-sm sm:text-base leading-relaxed">{message.message}</div>
              <div className={`text-xs mt-1 sm:mt-2 text-right ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {message.time}
              </div>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className={`px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-200'
            }`}>
              <div className="flex gap-1 items-center">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                }`}></div>
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                }`} style={{ animationDelay: '0.2s' }}></div>
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                }`} style={{ animationDelay: '0.4s' }}></div>
                <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {connectionStatus === 'connected' ? 'AI sedang mengetik...' : 'Memproses...'}
                </span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Templates & Input */}
      <div className={`flex-shrink-0 p-3 sm:p-4 border-t transition-colors duration-300 ${
        isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="mb-3">
          <p className={`text-xs sm:text-sm font-medium mb-2 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Pertanyaan Populer:
          </p>
          
          <div className="block sm:hidden mb-3">
            <div className="flex gap-2 overflow-x-auto pb-2" style={{ 
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
              {quickTemplates.slice(0, 4).map((template, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateClick(template)}
                  className={`flex-shrink-0 px-3 py-2 text-xs rounded-lg border whitespace-nowrap transition-all duration-300 transform active:scale-95 ${
                    isDarkMode 
                      ? 'border-green-600 bg-green-700 text-green-100 active:bg-green-600' 
                      : 'border-green-500 bg-green-50 text-green-700 active:bg-green-100'
                  }`}
                >
                  {template}
                </button>
              ))}
            </div>
          </div>
          
          <div className="hidden sm:flex flex-wrap gap-2 mb-3">
            {quickTemplates.map((template, index) => (
              <button
                key={index}
                onClick={() => handleTemplateClick(template)}
                className={`px-3 py-2 text-sm rounded-lg border transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'border-green-600 bg-green-700 text-green-100 hover:bg-green-600' 
                    : 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                {template}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Tanya tentang paket internet, IPTV, gaming, technical support..."
            className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 border rounded-xl text-sm sm:text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300 ${
              isDarkMode 
                ? 'bg-gray-700 text-white border-gray-600 focus:bg-gray-600' 
                : 'bg-gray-100 text-gray-900 border-gray-300 focus:bg-white'
            }`}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isTyping}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-all duration-300 transform active:scale-95 disabled:active:scale-100 text-sm sm:text-base"
          >
            {isTyping ? '...' : 'Kirim'}
          </button>
        </div>
        
        <p className={`text-xs mt-2 text-center transition-colors duration-300 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
           Spesialisasi: Paket Internet, IPTV, Gaming, Starlink, CCTV & Technical Support 24/7
        </p>
      </div>
    </div>
  );
};

export default ChatSupport;